import { BrowserRouter, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Cookies from "universal-cookie";
import { getUserSession } from "../api/user";
import { v4 as uuidv4 } from "uuid";
import { useAppContext } from "../contexts/AppContext";
import AnimatedRoutes from "./AnimatedRoutes";
import { hasTrackedEvent, trackBackendEvents } from "../utils/tracking";

function GoogleAdsPageTracker() {
  const location = useLocation();

  useEffect(() => {
    const blockedHosts = ["localhost", "127.0.0.1", "staging.inciteai.com"];

    if (
      blockedHosts.includes(window.location.hostname) ||
      typeof window.gtag !== "function"
    ) {
      return;
    }

    window.gtag("event", "page_view", {
      send_to: "AW-11333696455",
      page_path: `${location.pathname}${location.search}${location.hash}`,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [location.hash, location.pathname, location.search]);

  return null;
}

function StripeCheckoutTracker() {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkoutSessionId = params.get("checkout_session_id");
    if (!checkoutSessionId) {
      return;
    }

    const dedupeKey = `purchase:checkout_session:${checkoutSessionId}`;
    const clearCheckoutSessionParam = () => {
      const nextParams = new URLSearchParams(location.search);
      nextParams.delete("checkout_session_id");
      const nextSearch = nextParams.toString();
      const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}${location.hash}`;
      window.history.replaceState({}, document.title, nextUrl);
    };

    if (hasTrackedEvent(dedupeKey)) {
      clearCheckoutSessionParam();
      return;
    }

    let cancelled = false;

    const loadCheckoutTracking = async () => {
      try {
        const response = await fetch(
          `/stripe_checkout_tracking?checkout_session_id=${encodeURIComponent(checkoutSessionId)}`
        );
        const data = await response.json();

        if (!response.ok) {
          console.log("stripe checkout tracking error", data);
          return;
        }

        if (!cancelled && data.tracking_event) {
          trackBackendEvents(data.tracking_event);
        }

        if (!cancelled) {
          clearCheckoutSessionParam();
        }
      } catch (error) {
        console.log("stripe checkout tracking fetch error", error);
      }
    };

    loadCheckoutTracking();

    return () => {
      cancelled = true;
    };
  }, [location.hash, location.pathname, location.search]);

  return null;
}

function App() {
  const {
    userData,
    setUserData,
    loadingUserData,
    setLoadingUserData,
    loadingUserMessages,
    setIsNewUUID,
  } = useAppContext();



  const loadUserData = async (data1) => {
    const data = await getUserSession(data1.uuid, data1.current_session_id);
    let data2 = {};
    if (data.error) {
      // for other errors, redirect to sign in for proper session
      console.log("What are errors?", data.error);
      if (window.location.pathname != "/register") {
        // window.location.replace('/register')
      }
    } else if (data.uuidValid == false) {
      data2 = { requireSignIn: true, uuidHasAccount: true };
    } else {
      if (data.user_id != null) {
        // returned a user_id becasue active session
        data2 = data;
      } else {
        // return if uuid has account
        data2 = {
          uuidHasAccount: data.uuidHasAccount,
          free_unlimited_only: data.free_unlimited_only,
        };
      }
    }
    setUserData({ ...userData, ...data1, ...data2 });
  };

  // loading messages happens after, setLoadingUserData to false once loadingUserMessages is false
  useEffect(() => {
    if (!loadingUserMessages && loadingUserData) {
      setLoadingUserData(false);
    }
  }, [loadingUserMessages, loadingUserData]);

  useEffect(() => {
    const cookies = new Cookies();
    let uuid_value = cookies.get("uuid");
    if (!uuid_value) {
      cookies.set("uuid", `${uuidv4()}`, { path: "/" });
      uuid_value = cookies.get("uuid");
      setIsNewUUID(true);
    } else {
      setIsNewUUID(false);
    }

    // This is the unique id for this browser tab for the websocket!
    let current_session_id = uuidv4();

    let data1 = { uuid: uuid_value, current_session_id: current_session_id };
    loadUserData(data1);
  }, []);

  return (
    <div>
      <BrowserRouter>
        <GoogleAdsPageTracker />
        <StripeCheckoutTracker />
        <AnimatedRoutes />
      </BrowserRouter>
    </div>
  );
}

export default App;
